import type { SubtaskTreeRow, TaskReminder } from '@/src/renderer/lib/db/models'

/** Was eine Zeile aus ihrer Liste bekommt — dieselben Werte wie die Einzel-Hooks. */
export interface RowData {
  /** `undefined` bis geladen, wie `useSubtaskTree`. */
  subtaskTree: SubtaskTreeRow[] | undefined
  /** `0` bis geladen, wie `useCommentCount`. */
  commentCount: number
  /** `undefined` bis geladen, wie `useTaskReminders`. */
  reminders: TaskReminder[] | undefined
}

/** Eine Lieferung der drei Bündel-Abfragen, samt der Kennungen, für die sie gilt. */
export interface RowDataBatch {
  ids: readonly string[]
  subtaskTrees: ReadonlyMap<string, SubtaskTreeRow[]> | undefined
  commentCounts: ReadonlyMap<string, number> | undefined
  reminders: ReadonlyMap<string, TaskReminder[]> | undefined
}

export interface RowDataStore {
  /** `undefined`: Die Aufgabe gehört nicht zur Liste — die Zeile fragt dann selbst. */
  get(taskId: string): RowData | undefined
  subscribe(taskId: string, listener: () => void): () => void
  /**
   * Übernimmt eine Lieferung, **ohne** jemanden zu benachrichtigen — aufgerufen beim Rendern der Liste, damit
   * Zeilen, die im selben Durchgang entstehen, ihren Teil schon finden (sonst hinge jede für einen Durchgang
   * ihre drei Einzel-Abos ein). Gemeldet wird erst mit `flush`.
   */
  update(batch: RowDataBatch): void
  /** Meldet jede Zeile, deren Teil sich seit dem letzten `flush` geändert hat — nur diese. */
  flush(): void
  /** Wie viele Zeilen zuhören — für Tests. */
  listenerCount(): number
}

export function createRowDataStore(): RowDataStore {
  let entries = new Map<string, RowData>()
  const listeners = new Map<string, Set<() => void>>()
  const pending = new Set<string>()
  let last: RowDataBatch | null = null

  return {
    get: taskId => entries.get(taskId),

    subscribe(taskId, listener) {
      let set = listeners.get(taskId)
      if (!set) listeners.set(taskId, set = new Set())
      set.add(listener)
      return () => {
        set.delete(listener)
        if (set.size === 0) listeners.delete(taskId)
      }
    },

    update(batch) {
      if (last && last.ids === batch.ids && last.subtaskTrees === batch.subtaskTrees
        && last.commentCounts === batch.commentCounts && last.reminders === batch.reminders) return
      last = batch
      const next = new Map<string, RowData>()
      for (const id of batch.ids) {
        if (next.has(id)) continue
        const prev = entries.get(id)
        // Fehlt die Kennung in einer Karte, ist die Abfrage für die neue Menge noch unterwegs: den alten Stand halten.
        const subtaskTree = stable(prev?.subtaskTree, batch.subtaskTrees?.has(id) ? batch.subtaskTrees.get(id) : prev?.subtaskTree)
        const commentCount = batch.commentCounts?.get(id) ?? prev?.commentCount ?? 0
        const reminders = stable(prev?.reminders, batch.reminders?.has(id) ? batch.reminders.get(id) : prev?.reminders)
        if (prev && prev.subtaskTree === subtaskTree && prev.commentCount === commentCount && prev.reminders === reminders) {
          next.set(id, prev)
        } else {
          next.set(id, { subtaskTree, commentCount, reminders })
          pending.add(id)
        }
      }
      for (const id of entries.keys()) if (!next.has(id)) pending.add(id)
      entries = next
    },

    flush() {
      if (pending.size === 0) return
      const ids = [...pending]
      pending.clear()
      for (const id of ids) listeners.get(id)?.forEach(listener => listener())
    },

    listenerCount: () => [...listeners.values()].reduce((sum, set) => sum + set.size, 0),
  }
}

/**
 * Der alte Wert, solange der neue inhaltsgleich ist. Dexie schickt bei jedem Schreiben in `tasks` frische Objekte,
 * auch für Bäume, die sich nicht geändert haben — ohne das rendert jede Zeile der Liste neu.
 */
function stable<T>(prev: T, next: T): T {
  return samePlainData(prev, next) ? prev : next
}

/** Tiefer Vergleich für Datensätze: Zahlen, Texte, Daten (`Date`), Arrays und schlichte Objekte. */
export function samePlainData(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime()
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((item, i) => samePlainData(item, b[i]))
  }
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  const keysA = Object.keys(a)
  if (keysA.length !== Object.keys(b).length) return false
  return keysA.every(key => samePlainData((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]))
}
